import React, { useState, useEffect, useMemo } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    TableRow,
    Table,
    TableHead,
    TableBody,
    TableContainer,
    Paper,
    TableCell,
    IconButton,
    Divider,
    FormHelperText,
    Checkbox,
    FormControlLabel,
    Grid,
    Card,
    Box,
    Select,
    TextField,
    Typography,
    Chip,
    Tooltip,
    InputAdornment,
} from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import FilterListIcon from "@material-ui/icons/FilterList";
import SchoolIcon from "@material-ui/icons/School";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import NoteIcon from "@material-ui/icons/Note";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import StudentService from "./Locality/Service/studentService";
import SpecialObservationService from "./Locality/Service/specialObservationService";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";

const tableHeaders = ["Class", "Section", "Date", "View", "Edit", "Delete", "Add More"];

const formatDateDDMMYYYY = (value) => {
    if (!value) return "—";
    const s = String(value).trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        return `${dd}-${mm}-${d.getFullYear()}`;
    }
    return s;
};

const toDateInputValue = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmy) return `${dmy[3]}-${dmy[2]}-${dmy[1]}`;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return "";
};

const toStorageDate = (value) => {
    if (!value) return "";
    const s = String(value).trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    if (/^\d{2}-\d{2}-\d{4}$/.test(s)) return s;
    return s;
};

const useStyles = makeStyles((theme) => ({
    pageWrapper: {
        padding: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(1) },
    },
    pageHeader: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    pageTitle: {
        fontWeight: 700,
        fontSize: "1.9rem",
        color: "#0f2d4d",
        lineHeight: 1.2,
    },
    pageSubtitle: {
        color: "#6b7280",
        fontSize: "0.9rem",
        marginTop: 2,
    },
    primaryBtn: {
        backgroundColor: "#1769d6",
        color: "#fff",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        padding: "8px 18px",
        boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
        "&:hover": { backgroundColor: "#125bbd" },
    },
    noteBanner: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: 12,
        backgroundColor: "#eef4ff",
        border: "1px solid #d6e4ff",
        marginBottom: theme.spacing(2),
    },
    noteIconWrap: {
        width: 28,
        height: 28,
        borderRadius: "50%",
        backgroundColor: "#1769d6",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    filtersCard: {
        borderRadius: 14,
        border: "1px solid #e6eaf0",
        boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
        marginBottom: theme.spacing(2),
        overflow: "hidden",
    },
    filtersHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(1.5, 2),
        borderBottom: "1px solid #eef1f5",
        backgroundColor: "#fafbfc",
    },
    filtersBody: {
        padding: theme.spacing(2),
    },
    filterLabel: {
        fontSize: "0.78rem",
        fontWeight: 600,
        color: "#46566b",
        marginBottom: 6,
        display: "flex",
        alignItems: "center",
        gap: 6,
    },
    resetBtn: {
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        color: "#46566b",
    },
    tableCard: {
        borderRadius: 14,
        border: "1px solid #e6eaf0",
        boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
        overflow: "hidden",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        borderBottom: "1px solid #eef1f5",
    },
    iconBtnOutlined: {
        border: "1px solid #e6eaf0",
        borderRadius: 8,
        padding: 7,
    },
    headCell: {
        backgroundColor: "#f4f6fa",
        color: "#46566b",
        fontWeight: 700,
        fontSize: "0.8rem",
        borderBottom: "1px solid #e6eaf0",
        whiteSpace: "nowrap",
    },
    studentHeaderRow: {
        backgroundColor: "#eef4ff",
        "& td": {
            color: "#0f2d4d",
            fontWeight: 700,
            fontSize: "0.875rem",
            borderBottom: "1px solid #d6e4ff",
        },
    },
    noteExpandRow: {
        backgroundColor: "#f8fafc",
        "& td": {
            borderBottom: "1px solid #e6eaf0",
            paddingTop: theme.spacing(1.5),
            paddingBottom: theme.spacing(1.5),
        },
    },
    noteExpandBox: {
        display: "flex",
        alignItems: "flex-start",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 1.5),
        borderRadius: 10,
        border: "1px solid #e6eaf0",
        backgroundColor: "#fff",
    },
    classChip: {
        backgroundColor: "#eef4ff",
        color: "#1769d6",
        fontWeight: 600,
        height: 24,
    },
    sectionChip: {
        backgroundColor: "#f3efff",
        color: "#7c4dff",
        fontWeight: 600,
        height: 24,
    },
    iconAction: {
        color: "#1769d6",
        padding: 6,
    },
    deleteIcon: {
        color: "#c62828",
        padding: 6,
    },
    dialogPaper: {
        borderRadius: 14,
        maxWidth: 328,
        width: "100%",
    },
    dialogTitleBar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(2, 2.5),
        borderBottom: "1px solid #eef1f5",
        backgroundColor: "#fafafa",
    },
    dialogTitleText: {
        fontWeight: 700,
        fontSize: "1.15rem",
        color: "#0f2d4d",
    },
    dialogBody: {
        padding: theme.spacing(2, 2.5, 1.5),
    },
    fieldLabel: {
        display: "block",
        fontSize: "0.78rem",
        fontWeight: 600,
        color: "#46566b",
        marginBottom: 6,
    },
    sectionLabel: {
        display: "block",
        fontWeight: 700,
        color: "#46566b",
        fontSize: "0.72rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: theme.spacing(1.25),
    },
    formSection: {
        marginBottom: theme.spacing(2.5),
    },
    dialogFooter: {
        padding: theme.spacing(1.5, 2.5),
        borderTop: "1px solid #eef1f5",
    },
    cancelBtn: {
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        color: "#46566b",
    },
    saveBtn: {
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 8,
        backgroundColor: "#1769d6",
        color: "#fff",
        boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
        "&:hover": { backgroundColor: "#125bbd" },
    },
}));

export default function SpecialObservation() {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [pg, setPg] = useState(0);
    const [rpg, setRpg] = useState(10);
    const [noteOpen, setNoteOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedNoteId, setExpandedNoteId] = useState(null);
    const [addClassList, setAddClassList] = useState([]);
    const [sectionList, setSectionList] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [specialObservationList, setSpecialObservationList] = useState([]);
    const [filterClassId, setFilterClassId] = useState("");
    const [filterSectionId, setFilterSectionId] = useState("");
    const [filterSectionList, setFilterSectionList] = useState([]);
    const [observation, setObservation] = useState({
        studentId: "",
        date: "",
        specialObservation: "",
        classId: "",
        sectionId: "",
        isEventForAllStudent: false,
    });

    const validationSchema = Yup.object().shape({
        classId: Yup.string().when("isEventForAllStudent", {
            is: true,
            then: (schema) => schema.required("Class is required"),
            otherwise: (schema) => schema,
        }),
        studentId: Yup.string().when("isEventForAllStudent", {
            is: true,
            then: (schema) => schema.notRequired(),
            otherwise: (schema) => schema.required("Student Name is required"),
        }),
        isEventForAllStudent: Yup.boolean(),
        date: Yup.string().required("Date is required"),
        specialObservation: Yup.string(),
    });

    useEffect(() => {
        getSpecialObservationList();
        getAddClassList();
        return () => {
            setAddClassList([]);
            setStudentList([]);
            setSpecialObservationList([]);
        };
    }, []);

    const getStudentList = (event, obj) => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail"));
        const getSpeList = { classId: event.target.value };
        const effectiveSectionId = obj?.sectionId || formik?.values?.sectionId || "";
        if (effectiveSectionId) getSpeList.sectionId = effectiveSectionId;
        StudentService.getAllStudentById(userDetails.schooleId, getSpeList)
            .then((res) => {
                const studentDetails = res.map((row) => ({
                    _id: row._id,
                    studentName: `${row.firstName} ${row.lastName}`,
                }));
                setStudentList(studentDetails);
            })
            .catch(() => {});
    };

    const handleOpen = (reset) => {
        if (reset) {
            setObservation({
                studentId: "",
                date: "",
                specialObservation: "",
                classId: "",
                sectionId: "",
                isEventForAllStudent: false,
            });
        }
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const getFilterSectionListByClass = (classId) => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        if (!classId) {
            setFilterSectionList([]);
            return;
        }
        SectionService.findSectionList({ schooleId: userDetails.schooleId, classId })
            .then((res) => {
                const list = (Array.isArray(res) ? res : []).filter((x) => x?.status !== false);
                setFilterSectionList(list);
                if (filterSectionId && !list.some((s) => String(s?._id) === String(filterSectionId))) {
                    setFilterSectionId("");
                }
            })
            .catch(() => setFilterSectionList([]));
    };

    const onFilterClassChange = (e) => {
        const id = e.target.value;
        setFilterClassId(id);
        setFilterSectionId("");
        setPg(0);
        setExpandedNoteId(null);
        getFilterSectionListByClass(id);
        getSpecialObservationList({ classId: id, sectionId: "" });
    };

    const onFilterSectionChange = (e) => {
        const id = e.target.value;
        setFilterSectionId(id);
        setPg(0);
        setExpandedNoteId(null);
        getSpecialObservationList({ sectionId: id });
    };

    const handleResetFilters = () => {
        setFilterClassId("");
        setFilterSectionId("");
        setFilterSectionList([]);
        setSearchTerm("");
        setPg(0);
        setExpandedNoteId(null);
        getSpecialObservationList({ classId: "", sectionId: "" });
    };

    const getSpecialObservationList = (nextFilters = {}) => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail"));
        const classId = nextFilters.classId !== undefined ? nextFilters.classId : filterClassId;
        const sectionId = nextFilters.sectionId !== undefined ? nextFilters.sectionId : filterSectionId;
        SpecialObservationService.getAllSpecialObservation(userDetails.schooleId, {
            classId: classId || undefined,
            sectionId: sectionId || undefined,
        })
            .then((res) => setSpecialObservationList(Array.isArray(res) ? res : []))
            .catch(() => setSpecialObservationList([]));
    };

    const getAddClassList = () => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail"));
        AddClassService.getAllAddClass(userDetails.schooleId)
            .then((res) => setAddClassList((Array.isArray(res) ? res : []).filter((x) => x?.status !== false)))
            .catch(() => {});
    };

    const getSectionListByClass = (classId) => {
        const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
        if (!classId) {
            setSectionList([]);
            return;
        }
        SectionService.findSectionList({ schooleId: userDetails.schooleId, classId })
            .then((res) => {
                const list = (Array.isArray(res) ? res : []).filter((x) => x?.status !== false);
                setSectionList(list);
                const currentSectionId = String(formik.values.sectionId || "");
                if (currentSectionId && !list.some((s) => String(s?._id) === currentSectionId)) {
                    formik.setFieldValue("sectionId", "");
                }
            })
            .catch(() => setSectionList([]));
    };

    const editSpecialObservation = (row, addMore) => {
        const obj = JSON.parse(JSON.stringify(row));
        obj.studentId = row.studentId ? row.studentId._id : "";
        obj.classId = row.classId ? row.classId._id : "";
        obj.sectionId = row.sectionId ? row.sectionId._id || row.sectionId : "";
        obj.date = toDateInputValue(row.date);
        obj.isEventForAllStudent = false;

        getSectionListByClass(obj.classId);
        getStudentList({ target: { value: obj.classId } }, obj);
        setObservation(obj);
        if (addMore) delete obj._id;
        handleOpen(false);
    };

    const deleteSpecialObservation = (row) => {
        if (!row) return;
        const studentName = `${row?.studentId?.firstName || ""} ${row?.studentId?.lastName || ""}`.trim() || "this student";
        if (!window.confirm(`Delete special observation for "${studentName}" on ${formatDateDDMMYYYY(row.date)}?`)) return;
        SpecialObservationService.deleteSpecialobservation(row)
            .then(() => {
                if (expandedNoteId === row._id) setExpandedNoteId(null);
                getSpecialObservationList();
            })
            .catch((err) => {
                alert(err?.response?.data?.message || "Failed to delete observation.");
            });
    };

    const toggleViewNote = (id) => {
        setExpandedNoteId((prev) => (prev === id ? null : id));
    };

    const formik = useFormik({
        initialValues: observation,
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values, { resetForm }) => {
            const userDetails = JSON.parse(localStorage.getItem("userDetail"));
            const payload = {
                ...values,
                schooleId: userDetails.schooleId,
                date: toStorageDate(values.date),
                isEventForAllStudent: Boolean(values.isEventForAllStudent),
            };

            if (payload.isEventForAllStudent && !payload.classId) {
                alert("Please select a class first.");
                return;
            }

            if (observation._id) {
                delete payload.isEventForAllStudent;
                SpecialObservationService.upadeSpecialobservation({ ...payload, _id: observation._id })
                    .then(() => {
                        handleClose();
                        getSpecialObservationList();
                        resetForm();
                        alert("Special Observation Updated Successfully.");
                    })
                    .catch((err) => {
                        alert(err?.response?.data?.message || "Failed to update special observation.");
                    });
            } else {
                SpecialObservationService.creteSpecialobservation(payload)
                    .then((res) => {
                        getSpecialObservationList();
                        resetForm();
                        handleClose();
                        const count = res?.data?.count || res?.count;
                        alert(
                            count > 1
                                ? `Special Observation Added Successfully for ${count} students.`
                                : "Special Observation Added Successfully."
                        );
                    })
                    .catch((err) => {
                        alert(err?.response?.data?.message || err?.message || "Failed to add special observation.");
                    });
            }
        },
    });

    const filteredList = useMemo(() => {
        const base = Array.isArray(specialObservationList) ? specialObservationList : [];
        const term = searchTerm.trim().toLowerCase();
        return base.filter((o) => {
            if (filterClassId && String(o?.classId?._id || o?.classId || "") !== String(filterClassId)) return false;
            if (filterSectionId && String(o?.sectionId?._id || o?.sectionId || "") !== String(filterSectionId)) return false;
            if (!term) return true;
            const studentName = `${o?.studentId?.firstName || ""} ${o?.studentId?.lastName || ""}`.toLowerCase();
            const note = String(o?.specialObservation || "").toLowerCase();
            const className = String(o?.classId?.className || "").toLowerCase();
            const sectionName = String(o?.sectionId?.sectionName || "").toLowerCase();
            const dateStr = formatDateDDMMYYYY(o?.date).toLowerCase();
            return (
                studentName.includes(term) ||
                note.includes(term) ||
                className.includes(term) ||
                sectionName.includes(term) ||
                dateStr.includes(term)
            );
        });
    }, [specialObservationList, filterClassId, filterSectionId, searchTerm]);

    const groupedRows = useMemo(() => {
        const grouped = new Map();
        const sorted = [...filteredList].sort((a, b) => {
            const aName = `${a?.studentId?.firstName || ""} ${a?.studentId?.lastName || ""}`.trim().toLowerCase();
            const bName = `${b?.studentId?.firstName || ""} ${b?.studentId?.lastName || ""}`.trim().toLowerCase();
            if (aName !== bName) return aName.localeCompare(bName);
            return String(b?.date || "").localeCompare(String(a?.date || ""));
        });

        for (const obs of sorted) {
            const sid = obs?.studentId?._id || obs?.studentId || "unknown-student";
            const studentName = `${obs?.studentId?.firstName || ""} ${obs?.studentId?.lastName || ""}`.trim() || "—";
            const key = String(sid);
            if (!grouped.has(key)) grouped.set(key, { key, studentName, rows: [] });
            grouped.get(key).rows.push(obs);
        }
        return Array.from(grouped.values());
    }, [filteredList]);

    const pageGroups = groupedRows.slice(pg * rpg, pg * rpg + rpg);

    return (
        <Box className={classes.pageWrapper}>
            <Box className={classes.pageHeader}>
                <Box>
                    <Typography className={classes.pageTitle}>Special Observation</Typography>
                    <Typography className={classes.pageSubtitle}>
                        Record and review special observations for students by class and section.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    className={classes.primaryBtn}
                    startIcon={<AddIcon />}
                    onClick={() => handleOpen(true)}
                >
                    Add Observation
                </Button>
            </Box>

            {noteOpen && (
                <Box className={classes.noteBanner}>
                    <Box className={classes.noteIconWrap}>
                        <InfoOutlinedIcon style={{ fontSize: 18 }} />
                    </Box>
                    <Box flex={1}>
                        <Typography style={{ fontWeight: 600, color: "#1f3a5f", fontSize: "0.9rem" }}>
                            View observations
                        </Typography>
                        <Typography style={{ color: "#46566b", fontSize: "0.85rem", marginTop: 2, lineHeight: 1.6 }}>
                            Use the <strong>view icon</strong> in the table to expand and read the full observation note.
                            Dates are shown in <strong>dd-mm-yyyy</strong> format. Use <strong>Add More</strong> to add another note for the same student.
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setNoteOpen(false)}>
                        <CloseIcon style={{ fontSize: 18, color: "#7a8aa0" }} />
                    </IconButton>
                </Box>
            )}

            <Card elevation={0} className={classes.filtersCard}>
                <Box className={classes.filtersHeader}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                        <FilterListIcon style={{ fontSize: 18, color: "#1769d6" }} />
                        <Typography style={{ fontWeight: 700, color: "#0f2d4d", fontSize: "0.9rem" }}>
                            Filters
                        </Typography>
                        {(filterClassId || filterSectionId) && (
                            <Chip
                                size="small"
                                label="Filters active"
                                style={{ backgroundColor: "#eef4ff", color: "#1769d6", fontWeight: 600, height: 22 }}
                            />
                        )}
                    </Box>
                    <Button size="small" className={classes.resetBtn} onClick={handleResetFilters}>
                        Reset
                    </Button>
                </Box>
                <Box className={classes.filtersBody}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography className={classes.filterLabel}>
                                <SchoolIcon style={{ fontSize: 16, color: "#1769d6" }} />
                                Class
                            </Typography>
                            <FormControl variant="outlined" size="small" fullWidth>
                                <InputLabel id="filter-class-label">All Classes</InputLabel>
                                <Select
                                    labelId="filter-class-label"
                                    value={filterClassId}
                                    onChange={onFilterClassChange}
                                    label="All Classes"
                                >
                                    <MenuItem value=""><em>All Classes</em></MenuItem>
                                    {addClassList.map(({ _id, className }) => (
                                        <MenuItem key={_id} value={_id}>{className}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <Typography className={classes.filterLabel}>Section</Typography>
                            <FormControl variant="outlined" size="small" fullWidth disabled={!filterClassId}>
                                <InputLabel id="filter-section-label">All Sections</InputLabel>
                                <Select
                                    labelId="filter-section-label"
                                    value={filterSectionId}
                                    onChange={onFilterSectionChange}
                                    label="All Sections"
                                >
                                    <MenuItem value=""><em>All Sections</em></MenuItem>
                                    {filterSectionList.map(({ _id, sectionName }) => (
                                        <MenuItem key={_id} value={_id}>{sectionName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </Card>

            <Card elevation={0} className={classes.tableCard}>
                <Box className={classes.toolbar}>
                    <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
                        {groupedRows.length} Student{groupedRows.length !== 1 ? "s" : ""} · {filteredList.length} Observation{filteredList.length !== 1 ? "s" : ""}
                    </Typography>
                    <Box display="flex" alignItems="center" gridGap={10}>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Search students, notes, dates…"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPg(0); }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon style={{ fontSize: 18, color: "#9aa6b6" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Tooltip title="Refresh">
                            <IconButton className={classes.iconBtnOutlined} size="small" onClick={() => getSpecialObservationList()}>
                                <RefreshIcon style={{ fontSize: 20, color: "#46566b" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                <TableContainer component={Paper} elevation={0} style={{ maxHeight: 520 }}>
                    <Table stickyHeader size="small" className="dashboard-table-row-hover">
                        <TableHead>
                            <TableRow>
                                {tableHeaders.map((key) => (
                                    <TableCell key={key} className={classes.headCell}>{key}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageGroups.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={tableHeaders.length} align="center" style={{ padding: 48, color: "#7a8aa0" }}>
                                        No special observations found.
                                    </TableCell>
                                </TableRow>
                            )}
                            {pageGroups.flatMap((group) => [
                                <TableRow key={`student-${group.key}`} className={classes.studentHeaderRow}>
                                    <TableCell colSpan={tableHeaders.length}>
                                        {group.studentName}
                                    </TableCell>
                                </TableRow>,
                                ...group.rows.flatMap((row) => {
                                    const isExpanded = expandedNoteId === row._id;
                                    return [
                                        <TableRow key={row._id} hover>
                                            <TableCell>
                                                {row.classId?.className ? (
                                                    <Chip size="small" icon={<SchoolIcon style={{ fontSize: 14 }} />} label={row.classId.className} className={classes.classChip} />
                                                ) : "—"}
                                            </TableCell>
                                            <TableCell>
                                                {row.sectionId?.sectionName ? (
                                                    <Chip size="small" label={row.sectionId.sectionName} className={classes.sectionChip} />
                                                ) : "—"}
                                            </TableCell>
                                            <TableCell style={{ color: "#46566b", fontWeight: 600, whiteSpace: "nowrap" }}>
                                                {formatDateDDMMYYYY(row.date)}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={isExpanded ? "Hide note" : "View note"}>
                                                    <IconButton
                                                        className={classes.iconAction}
                                                        size="small"
                                                        onClick={() => toggleViewNote(row._id)}
                                                    >
                                                        {isExpanded ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Edit">
                                                    <IconButton className={classes.iconAction} size="small" onClick={() => editSpecialObservation(row)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Delete">
                                                    <IconButton className={classes.deleteIcon} size="small" onClick={() => deleteSpecialObservation(row)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Add more for this student">
                                                    <IconButton className={classes.iconAction} size="small" onClick={() => editSpecialObservation(row, true)}>
                                                        <AddIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>,
                                        isExpanded ? (
                                            <TableRow key={`${row._id}-note`} className={classes.noteExpandRow}>
                                                <TableCell colSpan={tableHeaders.length}>
                                                    <Box className={classes.noteExpandBox}>
                                                        <NoteIcon style={{ color: "#1769d6", marginTop: 2, flexShrink: 0 }} />
                                                        <Box>
                                                            <Typography style={{ fontSize: "0.72rem", fontWeight: 700, color: "#9aa6b6", textTransform: "uppercase", marginBottom: 4 }}>
                                                                Special Observation · {formatDateDDMMYYYY(row.date)}
                                                            </Typography>
                                                            <Typography style={{ color: "#0f2d4d", fontSize: "0.9rem", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                                                                {row.specialObservation?.trim() || "No observation text recorded."}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : null,
                                    ].filter(Boolean);
                                }),
                            ])}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={groupedRows.length}
                    page={pg}
                    onPageChange={(_, p) => setPg(p)}
                    rowsPerPage={rpg}
                    onRowsPerPageChange={(e) => { setRpg(parseInt(e.target.value, 10)); setPg(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Card>

            <Dialog open={open} onClose={handleClose} scroll="paper" fullWidth maxWidth="xs" PaperProps={{ className: classes.dialogPaper }}>
                <Box className={classes.dialogTitleBar}>
                    <span className={classes.dialogTitleText}>
                        {observation._id ? "Edit Observation" : "Add Observation"}
                    </span>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon style={{ fontSize: 20, color: "#7a8aa0" }} />
                    </IconButton>
                </Box>
                <form onSubmit={formik.handleSubmit}>
                    <DialogContent className={classes.dialogBody}>
                        <Box className={classes.formSection}>
                            <span className={classes.sectionLabel}>Class &amp; Student</span>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <span className={classes.fieldLabel}>Class</span>
                                    <FormControl variant="outlined" size="small" fullWidth error={formik.touched.classId && Boolean(formik.errors.classId)}>
                                        <Select
                                            name="classId"
                                            displayEmpty
                                            value={formik.values.classId}
                                            onChange={(e) => {
                                                formik.setFieldValue("sectionId", "");
                                                formik.handleChange(e);
                                                getSectionListByClass(e.target.value);
                                                getStudentList(e);
                                            }}
                                        >
                                            <MenuItem value=""><em>Select Class</em></MenuItem>
                                            {addClassList.map(({ _id, className }) => (
                                                <MenuItem key={_id} value={_id}>{className}</MenuItem>
                                            ))}
                                        </Select>
                                        {formik.touched.classId && formik.errors.classId && (
                                            <FormHelperText>{formik.errors.classId}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <span className={classes.fieldLabel}>Section (Optional)</span>
                                    <FormControl variant="outlined" size="small" fullWidth disabled={!formik.values.classId || sectionList.length === 0}>
                                        <Select
                                            name="sectionId"
                                            displayEmpty
                                            value={formik.values.sectionId || ""}
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                if (formik.values.classId) {
                                                    getStudentList({ target: { value: formik.values.classId } }, { sectionId: e.target.value || "" });
                                                }
                                            }}
                                        >
                                            <MenuItem value=""><em>All Sections</em></MenuItem>
                                            {sectionList.map(({ _id, sectionName }) => (
                                                <MenuItem key={_id} value={_id}>{sectionName}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <span className={classes.fieldLabel}>Student Name</span>
                                    <FormControl variant="outlined" size="small" fullWidth error={formik.touched.studentId && Boolean(formik.errors.studentId)}>
                                        <Select
                                            name="studentId"
                                            displayEmpty
                                            value={formik.values.studentId}
                                            onChange={formik.handleChange}
                                            disabled={Boolean(formik.values.isEventForAllStudent) || Boolean(observation._id)}
                                        >
                                            <MenuItem value=""><em>Select Student</em></MenuItem>
                                            {studentList.map(({ _id, studentName }) => (
                                                <MenuItem key={_id} value={_id}>{studentName}</MenuItem>
                                            ))}
                                        </Select>
                                        {formik.touched.studentId && formik.errors.studentId && (
                                            <FormHelperText>{formik.errors.studentId}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                {!observation._id && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="isEventForAllStudent"
                                                    color="primary"
                                                    checked={Boolean(formik.values.isEventForAllStudent)}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        formik.setFieldValue("isEventForAllStudent", checked);
                                                        if (checked) formik.setFieldValue("studentId", "");
                                                    }}
                                                />
                                            }
                                            label="For All Class Students"
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>

                        <Divider />

                        <Box className={classes.formSection} style={{ marginTop: 20, marginBottom: 0 }}>
                            <span className={classes.sectionLabel}>Observation Details</span>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <span className={classes.fieldLabel}>Date (saved as dd-mm-yyyy)</span>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        name="date"
                                        type="date"
                                        value={formik.values.date}
                                        onChange={formik.handleChange}
                                        error={formik.touched.date && Boolean(formik.errors.date)}
                                        helperText={formik.touched.date && formik.errors.date}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <span className={classes.fieldLabel}>Special Observation</span>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        name="specialObservation"
                                        placeholder="Write your observation..."
                                        multiline
                                        minRows={4}
                                        value={formik.values.specialObservation}
                                        onChange={formik.handleChange}
                                        error={formik.touched.specialObservation && Boolean(formik.errors.specialObservation)}
                                        helperText={formik.touched.specialObservation && formik.errors.specialObservation}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>

                    <DialogActions className={classes.dialogFooter}>
                        <Button onClick={handleClose} className={classes.cancelBtn}>Cancel</Button>
                        <Button type="submit" variant="contained" className={classes.saveBtn}>
                            {observation._id ? "Update" : "Add"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
}
